import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { Button, Input } from "../../../../design-system";
import {
  McpToken,
  createMcpToken,
  listMcpTokens,
  revokeMcpToken,
} from "../../../../database/mcpAccess/mcpAccessApi";

type Props = {
  communityId?: string;
  isActive?: boolean;
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const Intro = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
  max-width: 720px;
`;

const Row = styled.div`
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 260px;
  flex: 1;
`;

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const TokenCallout = styled.div`
  border: 1px solid var(--color-border);
  background: var(--color-surface-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const CalloutTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Mono = styled.code`
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  word-break: break-all;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  color: var(--color-text);
`;

const KeyValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const Warn = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-warning-text, #9a6700);
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.4fr 1.4fr 0.8fr auto;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const BodyRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.4fr 1.4fr 0.8fr auto;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  align-items: center;
  border-top: 1px solid var(--color-border);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const Muted = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
`;

const Empty = styled.div`
  padding: var(--space-6);
  text-align: center;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const ConnectCard = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 720px;
`;

const ConnectTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: var(--font-size-md, 16px);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0;
`;

const Steps = styled.ol`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
  padding-left: var(--space-5);
`;

const GuideLink = styled.a`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary, #5b8cff);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const AdvancedToggle = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  &:hover {
    color: var(--color-text);
  }
`;

const GUIDE_URL = "https://tribed.io/guides/connect-ai";
// Shown until listMcpTokens returns the server's value.
const DEFAULT_CONNECTOR_URL = "https://tribed-mcp.fly.dev/mcp";

const fmtDate = (ms?: number) =>
  ms ? new Date(ms).toLocaleDateString() : "—";

const AIAccessForm = ({ communityId, isActive }: Props) => {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [connectorUrl, setConnectorUrl] = useState<string>("");
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState(false);
  const [justCreated, setJustCreated] = useState<{
    token: string;
    connectorUrl: string;
    label: string;
  } | null>(null);

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listMcpTokens(communityId);
      setTokens(res.tokens || []);
      setConnectorUrl(res.connectorUrl || "");
    } catch (e: any) {
      setError(e?.message || "Could not load tokens");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    if (isActive) refresh();
  }, [isActive, refresh]);

  const onCreate = async () => {
    if (!communityId || !label.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const created = await createMcpToken(communityId, label.trim());
      setJustCreated({
        token: created.token,
        connectorUrl: created.connectorUrl,
        label: created.label,
      });
      setLabel("");
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not create token");
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (id: string) => {
    if (!communityId) return;
    setError(null);
    try {
      await revokeMcpToken(communityId, id);
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not revoke token");
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  return (
    <Wrap>
      <ConnectCard>
        <ConnectTitle>Connect Claude or ChatGPT</ConnectTitle>
        <Steps>
          <li>
            In Claude, open Settings, then Connectors, then Add custom
            connector. In ChatGPT, open Settings, then Apps and connectors,
            then Advanced, turn on Developer mode, and choose Create.
          </li>
          <li>Paste the connector URL below.</li>
          <li>
            A Tribed page opens. Sign in with the same account you use here.
            That is all. Your AI only sees this community.
          </li>
        </Steps>
        <KeyValue>
          <Muted>Connector URL</Muted>
          <Mono>{connectorUrl || DEFAULT_CONNECTOR_URL}</Mono>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => copy(connectorUrl || DEFAULT_CONNECTOR_URL)}
            >
              Copy URL
            </Button>
          </div>
        </KeyValue>
        <GuideLink href={GUIDE_URL} target="_blank" rel="noopener noreferrer">
          Open the step-by-step guide
        </GuideLink>
      </ConnectCard>

      {error && <Warn>{error}</Warn>}

      <AdvancedToggle
        type="button"
        aria-expanded={showKeys}
        onClick={() => setShowKeys((v) => !v)}
      >
        {showKeys ? "Hide access keys" : "Advanced: use an access key"}
        {tokens.length > 0 ? ` (${tokens.length})` : ""}
      </AdvancedToggle>

      {showKeys && (
        <>
          <Intro>
            Use a key when sign-in is not an option, for example a shared team
            account or a tool other than Claude or ChatGPT. On the Tribed page,
            choose Use an access key instead and paste it. Each key only reaches
            this community.
          </Intro>

          <Row>
            <Field>
              <Label htmlFor="mcp-label">Token name</Label>
              <Input
                id="mcp-label"
                placeholder="e.g. Maria's phone, Studio laptop"
                value={label}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setLabel(e.target.value)
                }
              />
            </Field>
            <Button
              variant="ai"
              onClick={onCreate}
              disabled={creating || !label.trim() || !communityId}
            >
              {creating ? "Generating..." : "Generate connector"}
            </Button>
          </Row>

          {justCreated && (
            <TokenCallout>
              <CalloutTitle>Connector ready: {justCreated.label}</CalloutTitle>
              <KeyValue>
                <Muted>Connector URL</Muted>
                <Mono>{justCreated.connectorUrl}</Mono>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copy(justCreated.connectorUrl)}
                  >
                    Copy URL
                  </Button>
                </div>
              </KeyValue>
              <KeyValue>
                <Muted>Token</Muted>
                <Mono>{justCreated.token}</Mono>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copy(justCreated.token)}
                  >
                    Copy token
                  </Button>
                </div>
              </KeyValue>
              <Warn>
                This token is shown only once. Copy it now and store it securely. If
                you lose it, revoke it and generate a new one.
              </Warn>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setJustCreated(null)}
                >
                  Done
                </Button>
              </div>
            </TokenCallout>
          )}

          <Table>
            <HeaderRow>
              <span>Name</span>
              <span>Created</span>
              <span>Last used</span>
              <span>Status</span>
              <span />
            </HeaderRow>
            {loading ? (
              <Empty>Loading...</Empty>
            ) : tokens.length === 0 ? (
              <Empty>No connectors yet. Generate one above.</Empty>
            ) : (
              tokens.map((t) => (
                <BodyRow key={t.id}>
                  <span>{t.label || <Muted>Unnamed</Muted>}</span>
                  <Muted>{fmtDate(t.createdAt)}</Muted>
                  <Muted>{fmtDate(t.lastUsedAt)}</Muted>
                  <span>{t.disabled ? <Muted>Revoked</Muted> : "Active"}</span>
                  <span>
                    {!t.disabled && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onRevoke(t.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </span>
                </BodyRow>
              ))
            )}
          </Table>
        </>
      )}
    </Wrap>
  );
};

export default AIAccessForm;
